// petconnect-v2/frontend/src/components/features/MapView/DualViewComponent.js
// UPDATED: Cards are clickable — navigate to /post/:id
// KEPT: Full-image fix (object-contain + padding-top aspect ratio)

import React, { useState } from 'react';
import { MapPin, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const DualViewComponent = ({ posts = [] }) => {
  const foundPosts   = (posts || []).filter(p => p.category === 'found');
  const missingPosts = (posts || []).filter(p => p.category === 'missing');

  const PostCard = ({ post, type }) => {
    const navigate = useNavigate();
    const [imgError, setImgError]         = useState(false);
    const [currentImgIdx, setCurrentImgIdx] = useState(0);

    const hasImages  = post.media_urls && post.media_urls.length > 0;
    const imageUrl   = hasImages ? post.media_urls[currentImgIdx] : null;
    const totalImages = hasImages ? post.media_urls.length : 0;

    const goToPost = () => navigate(`/post/${post.id}`);

    const nextImage = (e) => {
      e.stopPropagation();
      setCurrentImgIdx((prev) => (prev + 1) % totalImages);
      setImgError(false);
    };

    const prevImage = (e) => {
      e.stopPropagation();
      setCurrentImgIdx((prev) => (prev - 1 + totalImages) % totalImages);
      setImgError(false);
    };

    const accentColor = type === 'found'
      ? 'border-green-200 hover:border-green-400'
      : 'border-red-200 hover:border-red-400';
    const badgeBg   = type === 'found' ? 'bg-green-500'  : 'bg-red-500';
    const badgeText = type === 'found' ? '🟢 FOUND'      : '🔴 MISSING';

    return (
      <div
        onClick={goToPost}
        className={`border-2 rounded-xl overflow-hidden hover:shadow-lg transition bg-white cursor-pointer group ${accentColor}`}
      >
        {/* Image — padding-top trick for full image without cropping */}
        <div className="relative w-full bg-gray-50" style={{ paddingTop: '66.67%' }}>
          {hasImages && !imgError ? (
            <>
              <img
                src={imageUrl}
                alt={post.pet_name || 'Pet photo'}
                className="absolute inset-0 w-full h-full object-contain group-hover:opacity-95 transition"
                onError={() => setImgError(true)}
                loading="lazy"
              />
              {totalImages > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition z-10"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition z-10"
                  >
                    <ChevronRight size={16} />
                  </button>
                  <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1 z-10">
                    {post.media_urls.map((_, idx) => (
                      <div
                        key={idx}
                        className={`w-1.5 h-1.5 rounded-full transition ${idx === currentImgIdx ? 'bg-white' : 'bg-white/50'}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-5xl">🐾</div>
          )}

          {/* Category badge */}
          <div className={`absolute top-2 left-2 text-xs font-bold px-2 py-1 rounded-full text-white z-10 ${badgeBg}`}>
            {badgeText}
          </div>

          {/* View post hint on hover */}
          <div className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition z-10">
            <ExternalLink size={12} />
          </div>
        </div>

        {/* Card body */}
        <div className="p-3">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="font-bold text-gray-800 text-sm">
                {post.pet_name || 'Unknown Pet'}
                {post.pet_type && <span className="font-normal text-gray-500"> · {post.pet_type}</span>}
              </p>
              <p className="text-xs text-gray-500">
                by {post.author?.username || 'Unknown'} ·{' '}
                {post.created_at ? new Date(post.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            {post.status === 'resolved' && (
              <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-0.5 rounded-full flex-shrink-0">
                ✓ Reunited
              </span>
            )}
          </div>

          <p className="text-gray-600 text-xs mb-2 line-clamp-2">
            {post.content || 'No description provided.'}
          </p>

          {post.location_name && (
            <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
              <MapPin size={11} className="flex-shrink-0" />
              <span className="truncate">{post.location_name}</span>
            </div>
          )}

          {post.contact_info && (
            <div className={`p-2 rounded-lg text-xs font-semibold mt-2 ${
              type === 'found'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              📞 {post.contact_info}
            </div>
          )}

          <p className="text-xs text-gray-400 mt-2 opacity-0 group-hover:opacity-100 transition text-right">
            Tap to view full post →
          </p>
        </div>
      </div>
    );
  };

  const ColumnHeader = ({ count, type }) => (
    <div className={`flex items-center gap-3 mb-4 pb-3 border-b-2 ${type === 'found' ? 'border-green-500' : 'border-red-500'}`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-lg font-bold ${type === 'found' ? 'bg-green-500' : 'bg-red-500'}`}>
        {type === 'found' ? '🟢' : '🔴'}
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-800">{type === 'found' ? 'Found Pets' : 'Missing Pets'}</h3>
        <p className="text-sm text-gray-500">{count} {count === 1 ? 'post' : 'posts'}</p>
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl shadow-lg p-5">
        <ColumnHeader count={foundPosts.length} type="found" />
        <div className="space-y-4 max-h-[700px] overflow-y-auto pr-1">
          {foundPosts.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <div className="text-4xl mb-3">🟢</div>
              <p className="font-medium">No found pets in this area</p>
            </div>
          ) : (
            foundPosts.map((post) => <PostCard key={`found-${post.id}`} post={post} type="found" />)
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-5">
        <ColumnHeader count={missingPosts.length} type="missing" />
        <div className="space-y-4 max-h-[700px] overflow-y-auto pr-1">
          {missingPosts.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <div className="text-4xl mb-3">🔴</div>
              <p className="font-medium">No missing pets in this area</p>
            </div>
          ) : (
            missingPosts.map((post) => <PostCard key={`missing-${post.id}`} post={post} type="missing" />)
          )}
        </div>
      </div>
    </div>
  );
};

export default DualViewComponent;